import React, { InputHTMLAttributes, TextareaHTMLAttributes, forwardRef } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, leftIcon, rightIcon, className = '', ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-white dark:text-gray-700 mb-1.5 transition-colors duration-300">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500 transition-colors duration-300">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            className={`
              w-full rounded-lg border transition-all duration-200
              ${leftIcon ? 'pl-10' : 'pl-4'}
              ${rightIcon ? 'pr-10' : 'pr-4'}
              py-2.5
              ${error 
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                : 'border-dark-light dark:border-gray-300 focus:ring-accent dark:focus:ring-primary focus:border-transparent'
              }
              focus:outline-none focus:ring-2 focus:ring-opacity-20
              bg-dark dark:bg-white
              text-white dark:text-dark-dark
              placeholder:text-gray-400 dark:placeholder:text-gray-500
              disabled:bg-dark-light dark:disabled:bg-gray-50 disabled:text-gray-400 dark:disabled:text-gray-500
              ${className}
            `}
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500 transition-colors duration-300">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1.5 text-sm text-red-500">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1.5 text-sm text-gray-300 dark:text-gray-500 transition-colors duration-300">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, className = '', ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-white dark:text-gray-700 mb-1.5 transition-colors duration-300">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          className={`
            w-full rounded-lg border transition-all duration-200
            px-4 py-2.5
            ${error 
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
              : 'border-dark-light dark:border-gray-300 focus:ring-accent dark:focus:ring-primary focus:border-transparent'
            }
            focus:outline-none focus:ring-2 focus:ring-opacity-20
            bg-dark dark:bg-white
            text-white dark:text-dark-dark
            placeholder:text-gray-400 dark:placeholder:text-gray-500
            disabled:bg-dark-light dark:disabled:bg-gray-50 disabled:text-gray-400 dark:disabled:text-gray-500
            resize-none
            ${className}
          `}
          {...props}
        />
        {error && (
          <p className="mt-1.5 text-sm text-red-500">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1.5 text-sm text-gray-300 dark:text-gray-500 transition-colors duration-300">{helperText}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Input;